/**
 * 模組頁面代碼: 1330-UI
 * 模組說明: 住戶功能頁面
 * @module ResidentContainer
 */

// ----- import from local libraries -----
import {
    React,
    PropTypes,
    connect,
    BaseComponent,
    AccessModeEnums,
} from '../../components/BaseComponent';

// ----- import from local components -----
import Header from '../../features/main-frame/components/Header';
import Footer from '../../features/main-frame/components/Footer';
//import Sidebar from '../../features/main-frame/components/Sidebar';
import ResidentPage from './components/ResidentPage';
import ResidentModal from './components/ResidentModal';
import { ImportDataModal } from '../../components/ImportDataModal';
import { AlertModal, AlertModalModeEnums } from '../../components/AlertModal';
import { MessageModal, MessageModalModeEnums } from '../../components/MessageModal';
import ImportProgressModal from '../../components/ImportProgressModal';

// ----- import from local actions -----
import { fetchBuildingSelection } from '../../redux/actions/BuildingActions';
import {
    addResident,
    editResident,
    editResidentStatus,
    fetchResident,
    fetchResidentList,
    importResident,
} from '../../redux/actions/ResidentActions';
import {
    resetPassword,
    resetVenderId,
} from '../../redux/actions/ApplyActions';

// ----- constants and enums -----
const ResidentModalModeEnums = {
    NEW: 'new',
    EDIT: 'edit',
};

const ACTION_TARGET_RESIDENT = 'resident';

const RESPONSE_OK = 200;
const TITLE_RESIDENT_STATUS = '修改住戶狀態';
const TITLE_RESIDENT_IMPORT = '匯入住戶資料';
const TITLE_RESIDENT_PASSWORD = '重設住戶密碼';
const TITLE_RESIDENT_VENDERID = '重設住戶手機驗證碼';

class ResidentContainer extends BaseComponent {
    constructor(props) {
        super(props);
        // Ref
        this.residentModalRef = React.createRef();
        this.importDataModalRef = React.createRef();
        this.alertModalRef = React.createRef();
        this.messageModalRef = React.createRef();
        this.residentModal = null;
        this.importDataModal = null;
        this.alertModal = null;
        this.messageModal = null;

        this.state = {
            needResidentListLoad: false,
            residentModalMode: ResidentModalModeEnums.NEW,
            actionMode: AlertModalModeEnums.ENABLE,
            actionTarget: '',
            resident: null,
            alertMessage: '',
            alertMemo: '',
            actionIds: [],
            message: { mode: MessageModalModeEnums.ERROR, title: '', body: ''},
            search: { buildingId: 0, houseNum: 0, elevatorId: 0, floorDisplay: '' },
            isProgressModalOpen: false,
            progress: 0,
            statusMessage: '',
        };
        this.toggleResidentModal = this.toggleResidentModal.bind(this);
        this.toggleImportDataModal = this.toggleImportDataModal.bind(this);
        this.toggleAlertModal = this.toggleAlertModal.bind(this);
        this.onImportDataHandle = this.onImportDataHandle.bind(this);
        this.toggleMessageModal = this.toggleMessageModal.bind(this);
        this.onConfirm = this.onConfirm.bind(this);
    }

    toggleResidentModal(isShow, mode = ResidentModalModeEnums.NEW, index = null) {
        if (this.residentModal) {
            if (mode === ResidentModalModeEnums.EDIT) {
                this.setState({ resident: this.props.residentList[index] });
            } else {
                this.setState({ resident: null });
            }

            if (isShow) {
                this.residentModal.show();
            } else {
                this.residentModal.hide();
            }
            this.setState({ residentModalMode: mode });
        }
    }

    toggleImportDataModal(isShow) {
        if (this.importDataModal) {
            if (isShow) {
                this.importDataModal.show();
            } else {
                this.importDataModal.hide();
            }
        }
    }

    toggleAlertModal(isShow, mode = AlertModalModeEnums.ENABLE, target, message = '', residentIds = [], memo = '') {
        if (this.alertModal) {
            if (isShow) {
                let alertMessage = message;
                if (alertMessage === '') {
                    let length = residentIds.length;
                    if (mode === AlertModalModeEnums.ENABLE) {
                        alertMessage = `啟用 [ ${length} ] 筆資料`;
                    } else if (mode === AlertModalModeEnums.DISABLE) {
                        alertMessage = `停用 [ ${length} ] 筆資料`;
                    } else if (mode === AlertModalModeEnums.DELETE) {
                        alertMessage = `刪除 [ ${length} ] 筆資料`;
                    }
                }
                this.setState({ actionMode: mode });
                this.setState({ alertMessage: alertMessage });
                this.setState({ actionTarget: target});
                this.setState({ actionIds: residentIds });
                this.setState({ alertMemo: memo });
                this.alertModal.show();
            } else {
                this.alertModal.hide();
            }
        }
    }

    toggleMessageModal(isShow, mode = MessageModalModeEnums.INFO, title = '', message = '') {
        if (this.messageModal) {
            if (isShow) {
                let body = message;
                const statusCode = message?.code || '';
                if (statusCode !== '') {
                    body = `[ ${statusCode} ] ${message.message}`;
                }
                this.setState({ message: { mode: mode, title: title, body: body }});
                this.messageModal.show();
            } else {
                this.messageModal.hide();
            }
        }
    }

    findBuildingIdByName(buildingName) {
        let buildingId = 0;
        this.props.buildingSelection.forEach((building) => {
            if (building.buildingName === buildingName) {
                buildingId = building.buildingId;
            }
        });
        return buildingId;
    }

    findElevatorIdByNum(buildingId, elevatorNum) {
        let elevatorId = 0;
        const building = this.props.buildingSelection.find((building) => building.buildingId === buildingId);
        building.elevators.forEach((elevator) => {
            if (elevator.elevatorNum === elevatorNum) {
                elevatorId = elevator.elevatorId;
            }
        });

        return elevatorId;
    }

    onImportDataHandle(data) {
        let residents = [];

        // Open progress modal
        this.setState({ isProgressModalOpen: true, progress: 10, statusMessage: '匯入中...' });

        const interval = setInterval(() => {
            let progress = this.state.progress;
            if (progress < 100) {
                progress += 10;
                this.setState({ progress: progress, statusMessage: '匯入中...' });
            }
        }, 200);

        data.forEach((row) => {
            residents.push({
                buildingName: row['大樓名稱'],
                applicantName: row['姓名'],
                mobile: row['手機號碼'],
                houseNum: row['居住戶號'] !== '' ? parseInt(row['居住戶號']) : null,
                floorDisplay: row['居住樓層'],
                roomNum: row['居住室號'],
                elevatorNum: row['電梯編號'],
                applyFloor: row['申請樓層'],
                memo: row['備註'],
            });
        });

        this.props.importResident(residents).then((response) => {
            const statusCode = response.payload.code;
            if (statusCode !== RESPONSE_OK) {
                this.setState({ progress: 100, statusMessage: '匯入失敗' });
                this.toggleMessageModal(true, MessageModalModeEnums.ERROR, TITLE_RESIDENT_IMPORT, response.payload);
            } else {
                setTimeout(() => {
                    const { buildingId, houseNum, elevatorId, floorDisplay } = this.state.search;
                    this.props.fetchResidentList(buildingId, houseNum, elevatorId, floorDisplay).then(() => {
                        this.setState({ needResidentListLoad: true });
                        this.toggleImportDataModal(false);
                        // Close progress modal
                        this.setState({ progress: 100, statusMessage: '匯入完成' });
                        clearInterval(interval);
                        setTimeout(() => {
                            this.setState({ isProgressModalOpen: false });
                            // output import result
                            const message = response.payload.message || '';
                            if (message !== '') {
                                this.toggleMessageModal(true, MessageModalModeEnums.INFO, TITLE_RESIDENT_IMPORT, message);
                            }
                        }, 1000);
                    });
                }, 1000);
            }
        });
    }

    onConfirm() {
        const actionTarget = this.state.actionTarget;
        const actionMode = this.state.actionMode;
        const actionIds = this.state.actionIds;
        if (actionTarget === ACTION_TARGET_RESIDENT) {
            if (actionMode === AlertModalModeEnums.ENABLE || actionMode === AlertModalModeEnums.DISABLE) {
                let status = actionMode === AlertModalModeEnums.ENABLE ? 'A' : 'I';
                this.props.editResidentStatus(actionIds, status).then((response) => {
                    const statusCode = response.payload.code;
                    if (statusCode !== RESPONSE_OK) {
                        this.toggleAlertModal(false);
                        this.toggleMessageModal(true, MessageModalModeEnums.ERROR, TITLE_RESIDENT_STATUS, response.payload);
                    } else {
                        const { buildingId, houseNum, elevatorId, floorDisplay } = this.state.search;
                        this.props.fetchResidentList(buildingId, houseNum, elevatorId, floorDisplay).then(() => {
                            this.setState({ needResidentListLoad: true });
                            this.toggleAlertModal(false);
                        });
                    }
                });
            } else if (actionMode === AlertModalModeEnums.RESETPASSWORD) {
                this.props.resetPassword(actionIds[0]).then((response) => {
                    const statusCode = response.payload.code;
                    if (statusCode !== RESPONSE_OK) {
                        this.toggleAlertModal(false);
                        this.toggleMessageModal(true, MessageModalModeEnums.ERROR, TITLE_RESIDENT_PASSWORD, response.payload);
                    } else {
                        this.toggleAlertModal(false);
                        this.toggleMessageModal(true, MessageModalModeEnums.INFO, TITLE_RESIDENT_PASSWORD, "密碼重設成功");
                    }
                });
            } else if (actionMode === AlertModalModeEnums.RESETVENDERID) {
                this.props.resetVenderId(actionIds[0]).then((response) => {
                    const statusCode = response.payload.code;
                    if (statusCode !== RESPONSE_OK) {
                        this.toggleAlertModal(false);
                        this.toggleMessageModal(true, MessageModalModeEnums.ERROR, TITLE_RESIDENT_VENDERID, response.payload);
                    } else {
                        this.toggleAlertModal(false);
                        this.toggleMessageModal(true, MessageModalModeEnums.INFO, TITLE_RESIDENT_VENDERID, "手機驗證碼重設成功");
                    }
                });
            }
        }
    }

    componentDidMount() {
        // 頁面所需資料初始化
        this.props.fetchBuildingSelection();
        this.props.fetchResidentList(0, 0, 0, '').then(() => {
            this.setState({ needResidentListLoad: true });
        });
        this.residentModal = new bootstrap.Modal(this.residentModalRef.current);
        this.importDataModal = new bootstrap.Modal(this.importDataModalRef.current);
        this.alertModal = new bootstrap.Modal(this.alertModalRef.current);
        this.messageModal = new bootstrap.Modal(this.messageModalRef.current);
    }
    
    componentDidUpdate(prevProps, prevState) {
    }

    componentWillUnmount() {
        this.toggleResidentModal(false);
        this.toggleImportDataModal(false);
        this.toggleAlertModal(false);
        this.toggleMessageModal(false);
    }

    render() {
        return (
            <React.Fragment>
                <Header />
                <div className="header__Placeholder"></div>
                <div className="m__wrap">
                    <div className="m__contents">
                        {/* <Sidebar /> */}
                        <ResidentPage
                            {...this.props}
                            needResidentListLoad={this.state.needResidentListLoad}
                            setNeedResidentListLoad={(bLoad) => this.setState({ needResidentListLoad: bLoad })}
                            onNewResident={() => this.toggleResidentModal(true, ResidentModalModeEnums.NEW)}
                            onImportData={() => this.toggleImportDataModal(true)}
                            onEditResident={(index) => this.toggleResidentModal(true, ResidentModalModeEnums.EDIT, index)}
                            onEditResidentStatus={(mode, message, applicantId) => this.toggleAlertModal(true, mode, ACTION_TARGET_RESIDENT, message, [applicantId])}
                            onResetPassword={(mode, message, memo, applicantId) => this.toggleAlertModal(true, mode, ACTION_TARGET_RESIDENT, message, [applicantId], memo)}
                            onResetVenderId={(mode, message, memo, applicantId) => this.toggleAlertModal(true, mode, ACTION_TARGET_RESIDENT, message, [applicantId], memo)}
                            setSearchResident={(buildingId, houseNum, elevatorId, floorDisplay) => { 
                                this.setState({ search: { buildingId: buildingId, houseNum: houseNum, elevatorId: elevatorId, floorDisplay: floorDisplay } });
                            }}
                            onBatchHandle={(mode, applicantIds) => this.toggleAlertModal(true, mode, ACTION_TARGET_RESIDENT, '', applicantIds)}
                        />
                    </div>
                </div>
                <Footer />
               <ResidentModal 
                    {...this.props}
                    ref={this.residentModalRef}
                    mode={this.state.residentModalMode}
                    resident={this.state.resident}
                    searchResident={this.state.search}
                    setNeedResidentListLoad={(bLoad) => this.setState({ needResidentListLoad: bLoad })}
                    onErrorHandle={(title, message) => this.toggleMessageModal(true, MessageModalModeEnums.ERROR, title, message)}
                />
                <ImportDataModal
                    ref={this.importDataModalRef}
                    onDataHandle={(data) => this.onImportDataHandle(data)}
                />
                <AlertModal
                    ref={this.alertModalRef}
                    mode={this.state.actionMode}
                    message={this.state.alertMessage}
                    memo={this.state.alertMemo}
                    onConfirm={() => this.onConfirm()}
                    onCancel={() => this.setState({ needResidentListLoad: true })}
                />
                <MessageModal
                    ref={this.messageModalRef}
                    mode={this.state.message.mode}
                    title={this.state.message.title}
                    message={this.state.message.body}
                />
                <ImportProgressModal
                    isOpen={this.state.isProgressModalOpen}
                    progress={this.state.progress} 
                    statusMessage={this.state.statusMessage}
                    onClose={() => this.setState({ isProgressModalOpen: false })}
                />
            </React.Fragment>
        );
    }
}

ResidentContainer.propTypes = {
    userPermissions: PropTypes.array,
    buildingSelection: PropTypes.array,
    residentList: PropTypes.array,
};

ResidentContainer.defaultProps = {
    userPermissions: [],
    buildingSelection: [],
    residentList: []
};

function mapStateToProps(state) {
    return {
        userPermissions: state.access.userPermissions,
        buildingSelection: state.building.buildingSelection.data,
        residentList: state.resident.residentList.data,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        fetchBuildingSelection: () => {
            return dispatch(fetchBuildingSelection());
        },
        addResident: (resident) => {
            return dispatch(addResident(resident));
        },
        editResident: (resident) => {
            return dispatch(editResident(resident));
        },
        editResidentStatus: (applicantIds, status) => {
            return dispatch(editResidentStatus({ applicantIds, status }));
        },
        fetchResident: (residentId) => {
            return dispatch(fetchResident(residentId));
        },
        fetchResidentList: (buildingId, houseNum, elevatorId, floorDisplay) => {
            return dispatch(fetchResidentList({ buildingId, houseNum, elevatorId, floorDisplay }));
        },
        importResident: (residents) => {
            return dispatch(importResident({ residents }));
        },
        resetPassword: (applicantId) => {
            return dispatch(resetPassword({ applicantId }));
        },
        resetVenderId: (applicantId) => {
            return dispatch(resetVenderId({ applicantId }));
        },
    };    
}

export default connect(mapStateToProps, mapDispatchToProps)(ResidentContainer);